import React from 'react';
import { getCountryByCode } from 'constants/countries';
import { ProfileTradeMapRegion } from 'types/interfaces/profile';
import { GeometryDot } from './geometry';
import { TradeMapModel } from './model';

// Desktop hover tooltip (addendum 10): "{State}, {Country}", "{n} received ·
// {n} sent", and the live lines. Never a username: the model carries none.
// Dense mode (Asana 1218949343743567, decision 8): the dot is a LOCATION, so the
// counts line reads "{n} partners · {m} packages". The ticket's top-3 partner
// names cannot appear here: the public aggregate has no names, by rule.

/** "WA, United States"; the country name alone when there is no state */
export const regionLabel = (region: ProfileTradeMapRegion): string => {
  const country = region.country ? getCountryByCode(region.country)?.name ?? region.country : '';
  if (region.state && country) return `${region.state}, ${country}`;
  return region.state || country;
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

interface Props {
  dot: GeometryDot;
  model: TradeMapModel;
  width: number;
}

const TradeMapTooltip: React.FC<Props> = ({ dot, model, width }) => {
  const { partner } = dot;
  const homeState = model.home ? model.home.region.state || regionLabel(model.home.region) : null;
  // Flip to the left of the dot in the right third, so the tip never leaves the map
  const flip = dot.x > width * 0.66;
  return (
    <div
      className={`ptm-tooltip${flip ? ' ptm-tooltip--flip' : ''}`}
      style={{ left: dot.x, top: dot.y - dot.r - 8 }}
      role="tooltip"
      data-testid="ptm-tooltip"
    >
      <div className="ptm-tooltip-place">{regionLabel(partner.region)}</div>
      <div className="ptm-tooltip-counts">
        {dot.location ? (
          <>
            {dot.location.partners.toLocaleString('en-US')} {plural(dot.location.partners, 'partner', 'partners')} ·{' '}
            {dot.location.packages.toLocaleString('en-US')} {plural(dot.location.packages, 'package', 'packages')}
          </>
        ) : (
          <>
            {partner.received.toLocaleString('en-US')} received · {partner.sent.toLocaleString('en-US')} sent
          </>
        )}
      </div>
      {homeState && partner.liveInbound > 0 && (
        <div className="ptm-tooltip-live">
          {partner.liveInbound.toLocaleString('en-US')} on {plural(partner.liveInbound, 'its', 'their')} way to {homeState}
        </div>
      )}
      {homeState && partner.liveOutbound > 0 && (
        <div className="ptm-tooltip-live">
          {partner.liveOutbound.toLocaleString('en-US')} on {plural(partner.liveOutbound, 'its', 'their')} way from {homeState}
        </div>
      )}
    </div>
  );
};

export default TradeMapTooltip;
